import type {
  AuthRequestCredentials,
  Resource,
  PaywallStateManagerContextValue,
} from "@trello-powerup-monorepo/iframe-messenger-paywall-v2/parent";
import {
  ParentPaywallProvider,
  type ParentPaywallProviderProps,
} from "@trello-powerup-monorepo/iframe-messenger-paywall-v2/parent";
import { LoadingSpinner } from "@v2/shared/ui/custom/loadingSpinner.ui";
import React from "react";
import { useLocation, useNavigate } from "react-router";
import { PaywallErrorBoundary } from "./paywallErrorBoundary";
import { PaywallResourceQueries } from "@v2/entities/paywall/resource/resource.api";
import { SentryManager } from "@v2/shared/lib/sentry/sentry";
import { prodLog } from "@v2/shared/lib/logger";
import {
  useTrelloContext,
  useTrelloCredentials,
} from "@v2/entities/trello/iframe";
import {
  readCachedPaywallState,
  persistUnifiedPaywallState,
} from "@v2/shared/lib/paywall/prefetchAccessFromPaywall";
import {
  unifiedToParentState,
  parentToUnifiedState,
} from "@v2/shared/lib/paywall/paywallStateAdapter";

// AIDEV-NOTE: Performance optimized - stable callbacks prevent config recreation cascade
export const WithPaywallIframeMessenger = React.memo(
  function WithPaywallIframeMessenger({
    children,
  }: {
    children: React.ReactNode;
  }) {
    const location = useLocation();
    const navigate = useNavigate();
    const context = useTrelloContext({ required: false });
    const resources = React.useMemo(
      () => PaywallResourceQueries.resourceQuery(context),
      [context]
    );
    const [credentials] = useTrelloCredentials();

    const locationRef = React.useRef(location.pathname);
    locationRef.current = location.pathname;

    React.useEffect(() => {
      if (!context?.member) return;
      SentryManager.setUser(context.member);
    }, [context?.member]);

    if (!context) {
      return <LoadingSpinner text="Connecting to Trello..." />;
    }

    // AIDEV-NOTE: Stable function that reads from ref to avoid dependency array issues
    const cachedUnifiedState = React.useMemo(() => {
      if (!context?.member) return null;
      return (
        readCachedPaywallState(context.member, context.board)?.state ?? null
      );
    }, [context?.member, context?.board]);

    // AIDEV-NOTE: Seed paywall state to avoid iframe gating delay while verifying with live messenger
    const stateSeed = React.useMemo(
      () =>
        cachedUnifiedState
          ? unifiedToParentState(cachedUnifiedState)
          : undefined,
      [cachedUnifiedState]
    );

    const handleUnifiedState = React.useCallback(
      (nextState: PaywallStateManagerContextValue) => {
        if (!context?.member) return;

        const appId = "bulk-actions";
        persistUnifiedPaywallState({
          state: parentToUnifiedState(nextState),
          memberId: context.member,
          boardId: context.board,
          appId,
        });
      },
      [context?.member, context?.board]
    );

    const {
      appId,
      guardConfig,
      guardContentConfig,
      iframeProviderConfig,
      stateManagerConfig,
    }: ParentPaywallProviderProps = createPaywallConfig(
      locationRef,
      navigate,
      resources,
      credentials,
      stateSeed,
      handleUnifiedState
    );

    return (
      <PaywallErrorBoundary
        onError={(error, errorInfo) => {
          console.error("PaywallGuard Error:", error, errorInfo);
        }}
      >
        <ParentPaywallProvider
          appId={appId}
          guardConfig={guardConfig}
          guardContentConfig={guardContentConfig}
          iframeProviderConfig={iframeProviderConfig}
          stateManagerConfig={stateManagerConfig}
          onSessionId={(id: string) => {
            SentryManager.setPaywallSessionId(id);
          }}
        >
          {children}
        </ParentPaywallProvider>
      </PaywallErrorBoundary>
    );
  }
);

function createPaywallConfig(
  locationRef: React.MutableRefObject<string>,
  navigate: (path: string) => void,
  resources: Resource[],
  credentials: AuthRequestCredentials | undefined,
  stateSeed: PaywallStateManagerContextValue | undefined,
  onUnifiedState: ((state: PaywallStateManagerContextValue) => void) | undefined
) {
  // AIDEV-NOTE: Performance optimized config - reduced dependencies to prevent cascade re-renders
  const config: ParentPaywallProviderProps = React.useMemo(() => {
    const getCurrentLocation = () => locationRef.current;

    const stateManagerConfig: ParentPaywallProviderProps["stateManagerConfig"] =
      {
        hasAccessClbck: (resources) => {
          // Custom logic for bulk-actions-pup: check if any resource has access
          return resources.some((resource) => resource.hasAccess);
        },
        initialState: stateSeed ?? undefined,
        onStateChange: onUnifiedState,
      };

    const iframeProviderConfig: ParentPaywallProviderProps["iframeProviderConfig"] =
      {
        baseUrl: process.env.PAYWALL_URL!,
        trustedOrigins: [process.env.PAYWALL_URL!],
        debug: prodLog,
      };

    const guardContentConfig: ParentPaywallProviderProps["guardContentConfig"] =
      {
        hideIframe: getCurrentLocation() === "/authorize",
        overlayContent: (loadingText: string) => (
          <LoadingSpinner text={loadingText} />
        ),
        overlayStyling: {
          backgroundColor: "var(--background-color, #fdf9f6)",
          zIndex: 1000,
        },
      };

    const guardConfig: ParentPaywallProviderProps["guardConfig"] = {
      protectedPath: "/modal",
      profilePath: "/modal/profile",
      authorizePath: "/authorize",
      createProfilePath: "/createProfile",
      noAccessPath: "/noBoardAccess",

      getCurrentLocation,
      onNavigate: (path: string) => {
        navigate(path);
      },
      resources: resources,
      appId: "bulk-actions",
      credentials: credentials || {},
    };

    return {
      appId: guardConfig.appId,
      guardConfig,
      guardContentConfig,
      iframeProviderConfig,
      stateManagerConfig,
    };
  }, [resources, credentials, stateSeed, onUnifiedState]);
  return config;
}
